import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import BallotIcon from '@mui/icons-material/Ballot';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ContactsIcon from '@mui/icons-material/Contacts';
import Groups3Icon from '@mui/icons-material/Groups3';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

// Reference types accepted by the AI chat API (references[].type), with the
// read permission the backend checks and where the entity lives in the app.
// colorIndex matches the order used by ChatEntityPicker.
export const AI_ENTITY_TYPES = {
  project: {
    label: 'Project',
    icon: AccountTreeIcon,
    permission: 'project',
    colorIndex: 0,
    route: (id) => `/projects/${id}`,
  },
  company_lead: {
    label: 'Company lead',
    icon: Groups3Icon,
    permission: 'lead',
    colorIndex: 1,
    route: (id) => `/leads/companies/${id}`,
  },
  person_lead: {
    label: 'Person lead',
    icon: Groups3Icon,
    permission: 'lead',
    colorIndex: 1,
    route: (id) => `/leads/persons/${id}`,
  },
  invoice: {
    label: 'Invoice',
    icon: ReceiptIcon,
    permission: 'invoice',
    colorIndex: 2,
    route: (id) => `/invoices/${id}`,
  },
  offer: {
    label: 'Offer',
    icon: DescriptionIcon,
    permission: 'offer',
    colorIndex: 3,
    route: (id) => `/offers/${id}`,
  },
  order: {
    label: 'Order',
    icon: BallotIcon,
    permission: 'order',
    colorIndex: 4,
    route: (id) => `/orders/${id}`,
  },
  company: {
    label: 'Company',
    icon: ApartmentIcon,
    permission: 'company',
    colorIndex: 5,
    route: (id) => `/companies/${id}`,
  },
  contact: {
    label: 'Contact',
    icon: ContactsIcon,
    permission: 'contact',
    colorIndex: 6,
    route: (id) => `/contacts/${id}`,
  },
  imap_email: {
    label: 'Email',
    icon: EmailOutlinedIcon,
    permission: 'email-inbox',
    colorIndex: 7,
    route: (id) => `/emails/${id}`,
  },
};

// Detail routes that identify an entity without help from the scene.
// Scenes can enrich this (label, project) via useAiPageContext.
const ROUTE_PATTERNS = [
  [/^\/projects\/management\/(\d+)(\/|$)/, 'project'],
  [/^\/projects\/(\d+)\/?$/, 'project'],
  [/^\/invoices\/(\d+)\/?$/, 'invoice'],
  [/^\/offers\/(\d+)\/?$/, 'offer'],
  [/^\/orders\/(\d+)\/?$/, 'order'],
  [/^\/companies\/(\d+)\/?$/, 'company'],
  [/^\/contacts\/(\d+)\/?$/, 'contact'],
  [/^\/leads\/companies\/(\d+)\/?$/, 'company_lead'],
  [/^\/leads\/persons\/(\d+)\/?$/, 'person_lead'],
  [/^\/emails\/(\d+)\/?$/, 'imap_email'],
];

export function parseRouteContext(pathname) {
  for (const [pattern, type] of ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (match) return { type, id: Number(match[1]) };
  }
  return null;
}

export const referenceKey = (ref) => (ref ? `${ref.type}:${ref.id}` : null);

// The project a context belongs to decides which chats are shown
export function contextProjectId(context) {
  if (!context) return null;
  if (context.type === 'project') return Number(context.id);
  return context.projectId ? Number(context.projectId) : null;
}

// Labels of referenced entities are not part of the transcript, so they are
// remembered locally whenever the app knows them.
const LABEL_CACHE_KEY = 'aiChat.referenceLabels';
const LABEL_CACHE_LIMIT = 500;

function readLabelCache() {
  try {
    return JSON.parse(localStorage.getItem(LABEL_CACHE_KEY)) || {};
  } catch {
    return {};
  }
}

export function rememberReferenceLabel(ref) {
  if (!ref?.label) return;
  const key = referenceKey(ref);
  const cache = readLabelCache();
  if (cache[key] === ref.label) return;

  delete cache[key];
  cache[key] = ref.label;
  const keys = Object.keys(cache);
  keys
    .slice(0, Math.max(0, keys.length - LABEL_CACHE_LIMIT))
    .forEach((oldKey) => delete cache[oldKey]);

  try {
    localStorage.setItem(LABEL_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* storage may be full or blocked */
  }
}

export function lookupReferenceLabel(ref) {
  return readLabelCache()[referenceKey(ref)] || null;
}

// the API sends only the first line of a stored text document (where
// references and files name themselves); file names may hold umlauts
function decodeFirstLine(base64) {
  let binary;
  try {
    binary = atob(base64);
  } catch {
    return '';
  }
  let text;
  try {
    // the bytes are UTF-8 (TextDecoder is missing in the test environment)
    text = decodeURIComponent(escape(binary));
  } catch {
    text = binary;
  }
  return text.split('\n')[0];
}

// Entity references are stored as text documents whose first line reads
// "ExoPaper <type> (id <n>), provided by the user as context:"
export function parseReferenceAttachment(attachment) {
  if (attachment?.type !== 'base64-document' || !attachment.base64) return null;
  const firstLine = decodeFirstLine(attachment.base64);
  const match = firstLine.match(/^ExoPaper\s+(.+?)\s+\(id\s+(\d+)\)/i);
  if (!match) return null;

  // tolerate "company_lead", "companyLead" and "Company lead"
  const type = match[1]
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
  return { type, id: Number(match[2]) };
}

// Files the user attached arrive as laravel/ai attachments: images stored
// on a disk or as base64 (both carry the file name), and text-based files
// as text documents (the extracted text, or a note why the file could not
// be read) whose first line reads
// 'Attached file "<name>" (<type>, <size>), <origin>.' where the origin is
// 'ExoPaper file id <n>' or 'from the folder of project <n> (path "<path>")'.
const FILE_HEADER =
  /^Attached file "(.+?)" \(([^()]*)\), (?:ExoPaper file id (\d+)|from the folder of project (\d+) \(path "(.+)"\))\./;

export function parseFileAttachment(attachment) {
  if (!attachment) return null;
  if (attachment.type === 'stored-image' || attachment.type === 'base64-image')
    return { name: attachment.name || '', isImage: true };
  if (attachment.type === 'stored-document')
    return { name: attachment.name || '', isImage: false };
  if (attachment.type !== 'base64-document' || !attachment.base64) return null;

  const match = decodeFirstLine(attachment.base64).match(FILE_HEADER);
  if (!match) return null;
  const [, name, details, fileId, projectId, path] = match;
  const mimeType = details.split(',')[0].includes('/')
    ? details.split(',')[0].trim()
    : undefined;
  const file = { name, mimeType, isImage: false };
  if (projectId)
    return { ...file, source: 'project', projectId: Number(projectId), path };
  return { ...file, id: Number(fileId) };
}
